/**
 * SCRIPT mode, the premium languages — WHAT SPANISH, FRENCH, GERMAN, ITALIAN,
 * PORTUGUESE AND ENGLISH ADD TO THE WELSH DECK (job #480).
 *
 * Tom, 2026-09-27: "what about the other premium languages in script mode? I
 * think we should include them, with the same sounds for legos as an option we
 * can play with". They are written in letters the learner already reads, as
 * Welsh and Irish are, so they run on the Welsh deck family unchanged
 * (welshDeck.ts, welshOrder.ts, welshEngine.ts, welshDeal.ts): the course's own
 * LEGOs, seeds and pod-1 lines plus everyday items, read target→known,
 * known→target and typed on our own keyboard.
 *
 * What differs per language is small and lives here:
 *
 *   THE KEYBOARD — each accented letter a long press on its base key, as iOS
 *     and Android offer ñ from n and ß from s; the language's commonest mark
 *     drawn small in the key's corner on every card, whatever the answer, so
 *     the mark never gives the answer away. English has none.
 *
 *   THE DECOYS — the letters a speller of that language mixes up.
 *
 *   THE FOLD — French and Italian write an elided article or pronoun onto the
 *     next word (l'école, dell'acqua); the fold reads such a word as the word
 *     after the apostrophe for usefulness and foil distance, so "l'école" is
 *     as useful as "école" and "à l'école" sits near "une école".
 *
 *   PLAY THE WORD — unlike Welsh's, behind a switch (premiumWordAudio on the
 *     script_mode row of algorithm_config, default on), so Tom can compare the
 *     deck with and without the course's own clips.
 *
 * The English decks are one per course (eng_for_spa, eng_for_hin …): the
 * English is the template every English course shares, so the ladder is one
 * ladder; only the known side each learner reads it against differs.
 *
 * Pure: no Vue.
 */

/** The premium target languages read on the deck family. */
export const PREMIUM_DECK_LANGS: readonly string[] = ['spa', 'fra', 'deu', 'ita', 'por', 'eng']

export const isPremiumDeckLang = (lang: string | null | undefined): boolean =>
  PREMIUM_DECK_LANGS.includes((lang ?? '').toLowerCase().slice(0, 3))

/**
 * THE HOLD (Tom, 2026-09-28, r-2026-09-28-script-mode-for-the-other-paid):
 * Script mode for the premium languages is held until rethought — a card true
 * only inside its course, nada = anything, is no use on its own. OFF hides every
 * learner way in (the mode row, Settings, a shared link); the decks and the
 * builder stay as they are, and the free languages are untouched. Flip to true
 * to bring them back.
 */
export const PREMIUM_SCRIPT_MODE_OFFERED = false

/**
 * THE HOLD LIFTED FOR BUILD (job #80; Tom 2026-10-28: "I don't see any reason why
 * we couldn't have script mode for other Roman-based languages … learn how to put
 * things together, block by block, like we've done with the Welsh"). The rethink
 * the hold waited for is the Build card: these premium languages are offered
 * again, read on the deck with the sentence built from its own LEGO tiles and no
 * typing (welshBuild.ts BUILD_LANGS).
 *
 * WAVE 1 (job #131; Tom 2026-10-28: "Let's do the premium languages first"): Italian was
 * proven first; French, Spanish, Portuguese and German follow on the same design. The Mexican
 * Spanish and Brazilian Portuguese variants read the language's offer (lang is the first three letters).
 *
 * ENGLISH AS TARGET (job #629, the order r-2026-09-27 set): on every eng_for_* course
 * with a deck, the one English template read against each learner's own language.
 */
export const PREMIUM_BUILD_OFFERED: readonly string[] = ['ita', 'fra', 'spa', 'por', 'deu', 'eng']

/** Is Script mode offered to a learner in this language? False only for a held premium language. */
export const scriptModeOfferedFor = (lang: string | null | undefined, offered = PREMIUM_SCRIPT_MODE_OFFERED): boolean =>
  offered || !isPremiumDeckLang(lang) || PREMIUM_BUILD_OFFERED.includes((lang ?? '').toLowerCase().slice(0, 3))

/** Every accented letter each language writes, to its base key; the corner mark first. */
export const PREMIUM_ACCENTS: Readonly<Record<string, Readonly<Record<string, readonly string[]>>>> = {
  spa: { a: ['á'], e: ['é'], i: ['í'], o: ['ó'], u: ['ú', 'ü'], n: ['ñ'] },
  fra: { e: ['é', 'è', 'ê', 'ë'], a: ['à', 'â', 'æ'], c: ['ç'], i: ['î', 'ï'], o: ['ô', 'œ'], u: ['ù', 'û', 'ü'], y: ['ÿ'] },
  deu: { a: ['ä'], o: ['ö'], u: ['ü'], s: ['ß'] },
  ita: { a: ['à'], e: ['è', 'é'], i: ['ì'], o: ['ò'], u: ['ù'] },
  por: { a: ['ã', 'á', 'â', 'à'], e: ['é', 'ê'], i: ['í'], o: ['õ', 'ó', 'ô'], u: ['ú'], c: ['ç'] },
  eng: {},
}

/** The corner mark each key wears on every card. */
export const premiumCorner = (lang: string): Record<string, string> =>
  Object.fromEntries(Object.entries(PREMIUM_ACCENTS[lang] ?? {}).map(([k, v]) => [k, v[0]]))

/** The letters any Latin-alphabet speller mixes up: vowels among themselves, voiced and unvoiced pairs, doubled sounds. */
const LATIN_NEAR: Readonly<Record<string, string[]>> = {
  a: ['e', 'o'], e: ['i', 'a'], i: ['e', 'y'], o: ['u', 'a'], u: ['o', 'i'], y: ['i', 'j'],
  b: ['p', 'v', 'd'], p: ['b', 'q'], d: ['t', 'b'], t: ['d', 'f'], g: ['c', 'j', 'q'], c: ['k', 's', 'g'], k: ['c', 'q'], q: ['c', 'k'],
  f: ['v', 'p'], v: ['f', 'b', 'w'], w: ['v', 'u'], s: ['z', 'c'], z: ['s', 'x'], x: ['s', 'z'], j: ['g', 'y'],
  m: ['n'], n: ['m'], l: ['r'], r: ['l'], h: ['j', 'k'],
}
const withNear = (extra: Record<string, string[]>): Record<string, string[]> =>
  Object.fromEntries([...new Set([...Object.keys(LATIN_NEAR), ...Object.keys(extra)])].map(k => [k, [...new Set([...(extra[k] ?? []), ...(LATIN_NEAR[k] ?? [])])]]))

/** Per language, the decoys its spellers really reach for first. */
export const PREMIUM_NEAR: Readonly<Record<string, Readonly<Record<string, string[]>>>> = {
  spa: withNear({ b: ['v'], v: ['b'], y: ['l', 'i'], l: ['y'], h: ['j', 'g'], j: ['g', 'h'], g: ['j'], c: ['z', 's'], z: ['c', 's'] }),
  fra: withNear({ e: ['a', 'i', 'o'], a: ['e', 'o'], c: ['s', 'q'], s: ['c', 'x', 't'], t: ['s', 'd'], x: ['s'], g: ['j'], j: ['g'] }),
  deu: withNear({ e: ['i', 'a'], i: ['e', 'j'], v: ['f', 'w'], w: ['v'], z: ['s', 't'], s: ['z'], d: ['t'], t: ['d'], h: ['c'], c: ['h', 'k'], k: ['c', 'g'] }),
  ita: withNear({ c: ['g', 'h'], g: ['c', 'h'], h: ['c', 'g'], z: ['s'], s: ['z'], e: ['i'], i: ['e'] }),
  por: withNear({ s: ['z', 'c', 'x'], z: ['s'], x: ['s', 'c'], c: ['s', 'q'], o: ['u'], u: ['o'], e: ['i'], i: ['e'], j: ['g'], g: ['j'], l: ['r', 'h'], h: ['l', 'n'] }),
  eng: withNear({ c: ['k', 's'], k: ['c'], i: ['e', 'y'], e: ['i', 'a'], o: ['u', 'a'], u: ['o'] }),
}

/** An elided article or pronoun written onto the next word: l', d', qu', dell', nell' … */
const ELISION: Readonly<Record<string, RegExp>> = {
  fra: /^(?:l|d|j|m|n|s|t|c|qu|jusqu|lorsqu|puisqu)'(?=\p{L})/u,
  ita: /^(?:l|d|un|all|dell|nell|dall|sull|coll|quell|quest|c|m|t|s|v|ci)'(?=\p{L})/u,
}

/** The word a written word is read as for usefulness and foil distance: French and Italian elision folded. */
export function premiumRadical(lang: string, w: string): string {
  const s = w.toLowerCase()
  const re = ELISION[lang]
  return re ? s.replace(re, '') : s
}

/** The word and the word it folds to — the Welsh `radicals` for a premium language. */
export const premiumRadicals = (lang: string) => (w: string): Set<string> => new Set([w, premiumRadical(lang, w)])

/** The fold for foils and the mixed order, or none where the language writes no elision. */
export const premiumFold = (lang: string): ((w: string) => string) | undefined =>
  ELISION[lang] ? (w: string) => premiumRadical(lang, w) : undefined

/**
 * THE SWITCH for playing the word on a premium deck (Tom 2026-09-27: "as an
 * option we can play with"): `premiumWordAudio` on the `script_mode` row. On
 * unless the row says false, so a missing key plays; `?wordaudio=0` or `=1` on
 * the page's URL overrides it on that device for a side-by-side.
 */
export function premiumWordAudioOn(config: unknown, search = ''): boolean {
  const q = new URLSearchParams(search).get('wordaudio')
  if (q === '0' || q === 'off') return false
  if (q === '1' || q === 'on') return true
  const v = (config as Record<string, unknown> | null | undefined)?.premiumWordAudio
  return v !== false && v !== 'false'
}
